import type { InsuranceProviderPlanDetailViewModel } from '@generated';
import { editPlanBillingConfigurationOptions, getInsuranceProviderPlanDetailOptions } from '@generated';
import { Form } from '~/shared/forms/Form';
import { FooterButtonContainer } from '~/shared/forms/buttons/FooterButtonContainer';
import { FormSubmitButton } from '~/shared/forms/buttons/FormSubmitButton';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import Yup from '~/util/yup';

interface BillingSettingsTabProps {
	plan: InsuranceProviderPlanDetailViewModel;
}

const billingModelOptions = [
	{ id: 1, name: 'PDGM - Patient-Driven Groupings Model' },
	{ id: 2, name: 'PPS - Prospective Payment System' },
	{ id: 3, name: 'FFS - Fee-for-Service' }
];

const claimSubmissionModelOptions = [
	{ id: 1, name: 'EDI - Electronic Data Interchange' },
	{ id: 2, name: 'Portal Submission' },
	{ id: 3, name: 'Paper Claims' }
];

const claimSubmissionFrequencyOptions = [
	{ id: 1, name: 'Auto-Submit When Ready' },
	{ id: 2, name: 'Require Approval' }
];

export function BillingSettingsTab({ plan }: BillingSettingsTabProps) {
	return (
		<div className="mt-6">
			<div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
				<h3 className="mb-4 text-lg font-semibold text-gray-900">Billing Configuration</h3>
				<Form
					initialValues={{
						planId: plan.id,
						billingModelId: plan.billingModelId ?? null,
						claimSubmissionModelId: plan.claimSubmissionModelId ?? null,
						claimSubmissionFrequencyId: plan.claimSubmissionFrequencyId ?? null
					}}
					validationSchema={Yup.object().shape({
						billingModelId: Yup.number().label('Billing Model').nullable().required(),
						claimSubmissionModelId: Yup.number().label('Claim Submission Model').nullable().required(),
						claimSubmissionFrequencyId: Yup.number().label('Claim Submission Frequency').nullable().required()
					})}
					saveMutation={{
						...editPlanBillingConfigurationOptions,
						invalidateQueryKeys: [getInsuranceProviderPlanDetailOptions(plan.id).queryKey],
						onSuccessMessage: 'Billing configuration updated successfully'
					}}
				>
					{formHelpers => (
						<>
							{formHelpers.errorElement}
							<div className="space-y-6">
								<div className="grid grid-cols-2 gap-6">
									<SelectFormField name="billingModelId" label="Billing Model" options={billingModelOptions} />
									<SelectFormField
										name="claimSubmissionModelId"
										label="Claim Submission Model"
										options={claimSubmissionModelOptions}
									/>
									<SelectFormField
										name="claimSubmissionFrequencyId"
										label="Submission Frequency"
										options={claimSubmissionFrequencyOptions}
									/>
								</div>
								<FooterButtonContainer className="border-t border-gray-200 pt-4">
									<FormSubmitButton />
								</FooterButtonContainer>
							</div>
						</>
					)}
				</Form>
			</div>
		</div>
	);
}
